/*
Photo storage layer — keeps captured photo Blobs in the IndexedDB 'photos'
store, keyed by a UUID filename matching the one stored in CaptureEntity.
photoFilename. Symmetric with the Android File-system layout under
getExternalFilesDir(null)/captures/<uuid>.jpg, just persisted in IDB
because the web has no equivalent app-private filesystem.

Why IDB and not OPFS:
- IDB Blob support is universal and predates OPFS.
- Volumes are personal-scale (hundreds, not thousands of photos).
- The backup ZIP exporter can stream Blobs out of IDB just as easily.
*/

import { kStores, withTx } from './db'

export interface PhotoRecord
{
	filename: string
	blob: Blob
	takenAt: number
}

/*
Persist a photo. Uses `put` so a re-saved record (same filename) overwrites
cleanly — the filename is a UUID so collisions are not expected, but `put`
keeps the operation idempotent for retries.
*/
export async function savePhoto(inPhoto: PhotoRecord): Promise<void>
{
	return withTx([kStores.photos], 'readwrite', (inTx) => {
		return new Promise<void>((inResolve, inReject) => {
			const vReq = inTx.objectStore(kStores.photos).put(inPhoto)  // upsert by primary key
			vReq.onsuccess = () => inResolve()
			vReq.onerror = () => inReject(vReq.error)
		})
	})
}

/*
Load a photo Blob by filename. Returns null when the entry is missing —
common when a backup ZIP was imported with `photosMissing` rows; the UI
should fall back to a placeholder rather than treating this as fatal.
*/
export async function loadPhoto(inFilename: string): Promise<Blob | null>
{
	return withTx([kStores.photos], 'readonly', (inTx) => {
		return new Promise<Blob | null>((inResolve, inReject) => {
			const vReq = inTx.objectStore(kStores.photos).get(inFilename)
			vReq.onsuccess = () => {
				const vRecord = vReq.result as PhotoRecord | undefined  // undefined when not found
				inResolve(vRecord?.blob ?? null)
			}
			vReq.onerror = () => inReject(vReq.error)
		})
	})
}

/*
Delete a photo Blob by filename. Idempotent — IDB's delete() succeeds
silently when the key is absent, which suits us: a capture with no
photo (photoFilename === null) shouldn't trigger any state change here,
but a stale orphan blob is just as cleanly handled as a present one.
*/
export async function deletePhoto(inFilename: string): Promise<void>
{
	return withTx([kStores.photos], 'readwrite', (inTx) => {
		return new Promise<void>((inResolve, inReject) => {
			const vReq = inTx.objectStore(kStores.photos).delete(inFilename)
			vReq.onsuccess = () => inResolve()
			vReq.onerror = () => inReject(vReq.error)
		})
	})
}
